import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import PrepareAndPreview from '@/components/forms/CSVUploadForms/PrepareAndPreview'
import { fetchOrganizationUsersAndTags } from '@/reducers/organizations/organization.actions'
import { resetCsvState } from '@/reducers/organizations/organizationUsersCSV.redux'

export const BulkUserUploadButton = ({
  tagCategories,
  setUserFilter,
  setLoading,
  organizationFeatureFlags,
}) => {
  const dispatch = useDispatch()
  const params = useParams()
  const userOrganizationId = useSelector((state) => state.currentUser?.organizationid)
  const organizationid = params.organizationid || userOrganizationId
  const [open, setOpen] = useState(false)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const [isImporting, setIsImporting] = useState(false)

  const parsedUsers = useSelector((state) => state.organizationUsersCSV.users)
  const streamingComplete = useSelector((state) => state.organizationUsersCSV.streamingComplete)
  const hasUnsubmittedWork = !isEmpty(parsedUsers) && !streamingComplete

  const handleOpen = () => {
    dispatch(resetCsvState())
    setOpen(true)
  }

  const closeAndReset = () => {
    setConfirmDiscardOpen(false)
    setOpen(false)
    dispatch(resetCsvState())
  }

  const handleCloseAttempt = () => {
    // Don't let the user abandon an in-flight import — the server keeps
    // creating users even if we stop listening, and we'd lose results.
    if (isImporting) {
      return
    }
    if (hasUnsubmittedWork) {
      setConfirmDiscardOpen(true)
      return
    }
    closeAndReset()
  }

  // Memoised so the streamingComplete useEffect inside PrepareAndPreview
  // doesn't re-evaluate on every parent render. Identity changes only when
  // the relevant deps actually move.
  const handleUploadComplete = useCallback(
    ({ shouldClose }) => {
      if (shouldClose && setUserFilter) {
        setUserFilter('active')
      }
      dispatch(
        fetchOrganizationUsersAndTags({
          organizationId: organizationid,
          // setLoading flips the parent page to a Loader, which unmounts this
          // button and the open modal. Only let it fire when we're closing.
          setLoading: shouldClose ? setLoading : undefined,
          modalClose: shouldClose ? closeAndReset : undefined,
        })
      )
    },
    [dispatch, organizationid, setUserFilter, setLoading]
  )

  return (
    <>
      <Button primary data-testid="bulk-user-upload-default" onClick={handleOpen}>
        Bulk Upload Users
      </Button>
      <BasicModal
        show={open}
        title="Bulk upload users"
        size="large"
        onClose={handleCloseAttempt}
        canCloseOnDimmerClick={false}
        showCloseButton={!isImporting}
      >
        <PrepareAndPreview
          tagCategories={tagCategories}
          modalClose={handleCloseAttempt}
          onUploadComplete={handleUploadComplete}
          onImportingChange={setIsImporting}
          organizationFeatureFlags={organizationFeatureFlags}
        />
      </BasicModal>
      <BasicModal
        show={confirmDiscardOpen}
        title="Cancel importing users?"
        size="tiny"
        onClose={() => setConfirmDiscardOpen(false)}
        footer={
          <>
            <Button onClick={() => setConfirmDiscardOpen(false)}>Keep editing</Button>
            <Button critical onClick={closeAndReset}>
              Discard
            </Button>
          </>
        }
      >
        <p>You&apos;ll lose all the data you&apos;ve prepared. This can&apos;t be undone.</p>
      </BasicModal>
    </>
  )
}
